/**
 * `JSON.parse` returns `any`, which silently types whatever came back as
 * whatever the caller wants. These helpers return `unknown` (for external
 * input) or a branded key (for in-process round trips) instead.
 */

/**
 * Parse JSON from an untrusted source (storage, a socket, a query param).
 * Returns undefined for null/undefined input or invalid JSON; narrow the
 * result with a guard from `utils/typeGuards` before using it.
 */
export function safeJsonParse(raw: string | null | undefined): unknown {
  if (raw === null || raw === undefined) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed;
  } catch {
    // swallow-ok: Invalid JSON returns an untrusted undefined sentinel; schema guards reject it before cached session/capability/call state or import settings can be used.
    return undefined;
  }
}

/**
 * A JSON string that is known to encode a `T`. Used where a value is
 * stringified for a stable identity (a memo dependency or SWR key) and parsed
 * back in the same process, so the shape needs no runtime check.
 */
export type JsonKey<T> = string & { readonly __jsonKeyOf: T };

export function toJsonKey<T>(value: T): JsonKey<T> {
  // A JSON.stringify result branded with the type it encodes.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- branding the string produced from a T
  return JSON.stringify(value) as JsonKey<T>;
}

export function fromJsonKey<T>(key: JsonKey<T>): T {
  // Only a toJsonKey<T> result can be a JsonKey<T>, so this parses back to a T.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- round trip of a value produced by toJsonKey<T>
  return JSON.parse(key) as T;
}
